import { computed } from 'vue'

import { resolveProviderUuid } from '@/composables/useProviderAccess'
import { useIvProviderStore } from '@/stores/insurance/provider'
import { useUserStore } from '@/stores/user'

/**
 * Name for the navbar's provider chip (src/layouts/provider.vue). Must track
 * whichever provider is actually being viewed — resolveProviderUuid()'s route-first
 * resolution, same as the sidebar nav links (useProviderNavItems) and every
 * provider-scoped API call (providerBase()) — not the user store's `activeProvider`
 * getter, which only reflects the last provider switched to via PATCH
 * /me/active-provider (SelectProvider.vue). Those diverge whenever something routes
 * a multi-provider user, or a Vector admin, straight to a provider_uuid without going
 * through the switcher (a task/notification link, a bookmarked URL, an admin opening
 * a specific provider) — the chip would otherwise show the wrong provider's name.
 */
export function useCurrentProviderName() {
  const user = useUserStore()
  const providerStore = useIvProviderStore()

  return computed<string | null>(() => {
    const uuid = resolveProviderUuid()
    if (!uuid)
      return null

    // The user's own `/me/providers` list has an up-to-date name for the resolved
    // uuid whenever it's a provider the signed-in user actually belongs to (the
    // common case). Vector admins have no such entries — fall back to whatever
    // provider-scoped settings are currently loaded for that uuid instead.
    return user.providers.find(p => p.uuid === uuid)?.name
      ?? providerStore.settings?.organization_name
      ?? null
  })
}
